/**
 * DirectJobClient
 *
 * Calls Runhuman API directly with PAT authentication.
 * Uses customerApiRoutes from @runhuman/shared for type-safe route building;
 * all wire mechanics (auth, error parsing, retries) live in api-request.ts.
 */

import type { JobClient, ListTesterNotesParams } from './job-client.js';
import type { DirectConfig } from '../types.js';
import type {
  CreateTestJobRequest,
  CreateTestJobResponse,
  JobStatusResponse,
  TemplateListResponse,
  OrganizationListResponse,
  OrganizationMembersListResponse,
  OrganizationInviteResponse,
  InviteMemberRequest,
  ProjectListResponse,
  JobArtifactType,
  Template,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CreateJobScheduleRequest,
  UpdateJobScheduleRequest,
  JobSchedule,
  ProjectScheduleListResponse,
  TesterNoteDetail,
  TesterNoteListResponse,
  CreateTesterNoteRequest,
  UpdateTesterNoteRequest,
  BakedDemoVideoStatusResponse,
  RerunTestJobRequest,
} from '@runhuman/shared';
import { customerApiRoutes, API_PREFIX } from '@runhuman/shared';
import { requestJson, type ApiRequestOptions } from './api-request.js';

/** Retries + per-attempt timeout for the polling path (getJobStatus). */
const POLL_RETRIES = 2;
const POLL_TIMEOUT_MS = 30_000;

export class DirectJobClient implements JobClient {
  constructor(private config: DirectConfig) {}

  private request<T>(routePath: string, options?: ApiRequestOptions): Promise<T> {
    return requestJson<T>(this.config, `${API_PREFIX}${routePath}`, options);
  }

  createJob(args: CreateTestJobRequest): Promise<CreateTestJobResponse> {
    return this.request(customerApiRoutes.jobs.build(), { method: 'POST', body: args });
  }

  rerunJob(jobId: string, overrides: RerunTestJobRequest): Promise<CreateTestJobResponse> {
    return this.request(customerApiRoutes.jobRerun.build({ jobId }), { method: 'POST', body: overrides });
  }

  getJobStatus(jobId: string, retries = POLL_RETRIES): Promise<JobStatusResponse> {
    return this.request(customerApiRoutes.jobStatus.build({ jobId }), {
      // Polling reuses connections aggressively; `Connection: close` keeps a
      // stale keep-alive socket from poisoning the next poll.
      headers: { Connection: 'close' },
      notFoundMessage: `Job not found: ${jobId}`,
      retries,
      timeoutMs: POLL_TIMEOUT_MS,
    });
  }

  getJobArtifact(jobId: string, artifactType: JobArtifactType): Promise<Record<string, unknown>> {
    return this.request(customerApiRoutes.jobArtifact.build({ jobId, artifactType }), {
      notFoundMessage: `Job not found: ${jobId}`,
    });
  }

  listTemplates(projectId: string, limit?: number): Promise<TemplateListResponse> {
    // AI agents asking for available templates want to see everything,
    // including the system defaults the web hides by default. Mirror the
    // pre-#3206 behaviour by explicitly opting defaults back in here.
    const query: Record<string, string> = { includeDefaults: 'true' };
    if (limit) query.limit = String(limit);

    return this.request(customerApiRoutes.projectTemplates.build({ projectId }), { query });
  }

  createTemplate(projectId: string, data: CreateTemplateRequest): Promise<Template> {
    return this.request(customerApiRoutes.projectTemplates.build({ projectId }), { method: 'POST', body: data });
  }

  updateTemplate(projectId: string, templateId: string, data: UpdateTemplateRequest): Promise<Template> {
    return this.request(customerApiRoutes.projectTemplate.build({ projectId, templateId }), { method: 'PATCH', body: data });
  }

  deleteTemplate(projectId: string, templateId: string): Promise<void> {
    return this.request(customerApiRoutes.projectTemplate.build({ projectId, templateId }), { method: 'DELETE' });
  }

  listOrganizations(): Promise<OrganizationListResponse> {
    return this.request(customerApiRoutes.organizations.build());
  }

  listOrganizationMembers(organizationId: string): Promise<OrganizationMembersListResponse> {
    return this.request(customerApiRoutes.organizationMembers.build({ organizationId }));
  }

  inviteOrganizationMember(organizationId: string, data: InviteMemberRequest): Promise<OrganizationInviteResponse> {
    return this.request(customerApiRoutes.organizationInvite.build({ organizationId }), { method: 'POST', body: data });
  }

  listProjects(organizationId?: string): Promise<ProjectListResponse> {
    const routePath = organizationId
      ? customerApiRoutes.organizationProjects.build({ organizationId })
      : customerApiRoutes.projects.build();

    return this.request(routePath);
  }

  listSchedules(projectId: string, limit?: number): Promise<ProjectScheduleListResponse> {
    const query: Record<string, string> = {};
    if (limit) query.limit = String(limit);

    return this.request(customerApiRoutes.projectSchedules.build({ projectId }), { query });
  }

  createSchedule(projectId: string, data: CreateJobScheduleRequest): Promise<JobSchedule> {
    return this.request(customerApiRoutes.projectSchedules.build({ projectId }), { method: 'POST', body: data });
  }

  updateSchedule(projectId: string, scheduleId: string, data: UpdateJobScheduleRequest): Promise<JobSchedule> {
    return this.request(customerApiRoutes.projectSchedule.build({ projectId, scheduleId }), { method: 'PATCH', body: data });
  }

  deleteSchedule(projectId: string, scheduleId: string): Promise<void> {
    return this.request(customerApiRoutes.projectSchedule.build({ projectId, scheduleId }), { method: 'DELETE' });
  }

  listTesterNotes(params?: ListTesterNotesParams): Promise<TesterNoteListResponse> {
    const query: Record<string, string> = {};
    if (params?.search) query.search = params.search;
    if (params?.tags && params.tags.length > 0) query.tags = params.tags.join(',');
    if (params?.organizationId) query.organizationId = params.organizationId;
    if (params?.projectId) query.projectId = params.projectId;
    if (params?.limit) query.limit = String(params.limit);
    if (params?.offset) query.offset = String(params.offset);

    return this.request(customerApiRoutes.testerNotes.build(), { query });
  }

  createTesterNote(data: CreateTesterNoteRequest): Promise<TesterNoteDetail> {
    return this.request(customerApiRoutes.testerNotes.build(), { method: 'POST', body: data });
  }

  updateTesterNote(noteId: string, data: UpdateTesterNoteRequest): Promise<TesterNoteDetail> {
    return this.request(customerApiRoutes.testerNote.build({ noteId }), { method: 'PATCH', body: data });
  }

  archiveTesterNote(noteId: string): Promise<{ success: boolean }> {
    return this.request(customerApiRoutes.testerNoteArchive.build({ noteId }), { method: 'POST', body: {} });
  }

  triggerBakedDemoVideoRender(jobId: string): Promise<BakedDemoVideoStatusResponse> {
    return this.request(customerApiRoutes.bakedDemoVideoRender.build({ jobId }), { method: 'POST', body: {} });
  }

  getBakedDemoVideoStatus(jobId: string): Promise<BakedDemoVideoStatusResponse> {
    return this.request(customerApiRoutes.bakedDemoVideoStatus.build({ jobId }));
  }
}
